// packages/api/src/hooks/admin/useCommissionExtract.ts
//
// Extrato admin (saldos, 3 visões) + fluxo NF (release/invoice/pay/cancel).
// Backend: /admin/commissions/{balances,consultants/:id/statement{,/entries}}.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  BulkReleaseCommissionPayload,
  BulkReleaseCommissionResult,
  CancelBalancePayload,
  ClearBalancePendencyPayload,
  CommissionAttentionCount,
  CommissionBalance,
  CommissionBalanceDetail,
  CommissionBalanceMessage,
  ConsultantStatement,
  ListCommissionBalancesFilters,
  ListStatementEntriesParams,
  MarkInvoicedPayload,
  MarkPaidPayload,
  PaginatedResponse,
  PostThreadMessageDto,
  ReschedulePaymentPayload,
  ResolveBalancePendencyByAdminPayload,
  ResolveDisputeDto,
  RevertCommissionBalancePayload,
  SendCommissionEmailPayload,
  SendCommissionEmailResult,
  SetBalancePendencyPayload,
  StatementEntryRow,
} from '@alamo/types';

import { apiClient } from '../../client';
import { queryKeys } from '../../query-keys';

interface BackendList<T> {
  count: number;
  rows: T[];
}

export function useCommissionBalances(filters?: ListCommissionBalancesFilters) {
  const page = filters?.page ?? 1;
  const limit = filters?.limit ?? 25;
  return useQuery({
    queryKey: queryKeys.commissionBalances.list(filters as Record<string, unknown> | undefined),
    queryFn: async (): Promise<PaginatedResponse<CommissionBalance>> => {
      const response = await apiClient.get<ApiSuccess<BackendList<CommissionBalance>>>(
        '/admin/commissions/balances',
        { params: filters },
      );
      const raw = response.data.data;
      return {
        items: raw.rows,
        total: raw.count,
        page,
        limit,
        totalPages: Math.max(1, Math.ceil(raw.count / limit)),
      } satisfies PaginatedResponse<CommissionBalance>;
    },
  });
}

/**
 * Contador de atenção do financeiro — alimenta o badge do menu admin de comissão
 * (mesmo papel do contador de aprovações pendentes do Pricing). `total` é o número
 * que pulsa no badge; 0 esconde. GET /admin/commissions/attention-count.
 * Permission commissions:manage. Atualização principal via push de socket
 * (`commissions.attention.count_changed`, assinado pelo badge); o polling de 5min
 * + refetch ao focar/reconectar são rede de segurança caso o socket caia.
 */
export function useCommissionAttentionCount(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.commissionAttention.count(),
    queryFn: async (): Promise<CommissionAttentionCount> => {
      const response = await apiClient.get<ApiSuccess<CommissionAttentionCount>>(
        '/admin/commissions/attention-count',
      );
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
    refetchInterval: 5 * 60_000,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useConsultantStatement(
  consultantProfileId: string | undefined,
  competence: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.commissionStatement.detail(consultantProfileId ?? '', competence ?? ''),
    queryFn: async (): Promise<ConsultantStatement> => {
      const response = await apiClient.get<ApiSuccess<ConsultantStatement>>(
        `/admin/commissions/consultants/${consultantProfileId}/statement`,
        { params: { competence } },
      );
      return response.data.data;
    },
    enabled: !!consultantProfileId && !!competence,
  });
}

/**
 * Drill-down paginado das entries do extrato (default 10/página).
 * GET /admin/commissions/consultants/:id/statement/entries —
 * section own = kind own+absorbed; team = kind override (sem boleto: `search`
 * em team retorna vazio). Sem apuração na competência → count 0 (não 404).
 */
export function useConsultantStatementEntries(
  consultantProfileId: string | undefined,
  params: ListStatementEntriesParams,
) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;
  return useQuery({
    queryKey: queryKeys.commissionStatement.entries(
      consultantProfileId ?? '',
      params as Record<string, unknown>,
    ),
    queryFn: async (): Promise<PaginatedResponse<StatementEntryRow>> => {
      const response = await apiClient.get<ApiSuccess<BackendList<StatementEntryRow>>>(
        `/admin/commissions/consultants/${consultantProfileId}/statement/entries`,
        { params },
      );
      const raw = response.data.data;
      return {
        items: raw.rows,
        total: raw.count,
        page,
        limit,
        totalPages: Math.max(1, Math.ceil(raw.count / limit)),
      } satisfies PaginatedResponse<StatementEntryRow>;
    },
    enabled: !!consultantProfileId && !!params.competence,
  });
}

/**
 * Detalhe do saldo com anexos resolvidos (presigned URLs TTL 1h).
 * GET /admin/commissions/balances/:id — attachments.{invoices,receipts}.
 * Errors: 404 COMMISSION_BALANCE_NOT_FOUND.
 */
export function useCommissionBalanceDetail(
  id: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.commissionBalances.detail(id ?? ''),
    queryFn: async (): Promise<CommissionBalanceDetail> => {
      const response = await apiClient.get<ApiSuccess<CommissionBalanceDetail>>(
        `/admin/commissions/balances/${id}`,
      );
      return response.data.data;
    },
    enabled: (options?.enabled ?? true) && !!id,
  });
}

// ─── NF / payout state machine ────────────────────────────────────────────────

function useBalanceMutation<TPayload>(action: 'release' | 'invoice' | 'pay' | 'cancel' | 'revert') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload?: TPayload;
    }): Promise<CommissionBalance> => {
      const response = await apiClient.patch<ApiSuccess<CommissionBalance>>(
        `/admin/commissions/balances/${id}/${action}`,
        payload ?? {},
      );
      return response.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.commissionBalances.all });
    },
  });
}

export function useReleaseBalance() {
  return useBalanceMutation<undefined>('release');
}
export function useInvoiceBalance() {
  return useBalanceMutation<MarkInvoicedPayload>('invoice');
}
export function usePayBalance() {
  return useBalanceMutation<MarkPaidPayload>('pay');
}
export function useCancelBalance() {
  return useBalanceMutation<CancelBalancePayload>('cancel');
}
/**
 * Revert a balance ONE step back (paid → invoiced → released → pending).
 * PATCH /admin/commissions/balances/:id/revert — reason is mandatory (audited).
 * Errors: 404 COMMISSION_BALANCE_NOT_FOUND, 409 COMMISSION_BALANCE_INVALID_TRANSITION.
 */
export function useRevertBalance() {
  return useBalanceMutation<RevertCommissionBalancePayload>('revert');
}

// ─── Pendência manual (Phase 36) ──────────────────────────────────────────────
// Registro e controle — NÃO bloqueia transições; auditado no history drawer.

/**
 * Marca pendência manual no saldo (motivo obrigatório 5..500 chars; re-marcar
 * sobrescreve). PATCH /admin/commissions/balances/:id/pendency.
 * Errors: 404 COMMISSION_BALANCE_NOT_FOUND.
 */
export function useSetBalancePendency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: SetBalancePendencyPayload;
    }): Promise<CommissionBalance> => {
      const response = await apiClient.patch<ApiSuccess<CommissionBalance>>(
        `/admin/commissions/balances/${id}/pendency`,
        payload,
      );
      return response.data.data;
    },
    // Invalida o detalhe + o fio do saldo (que alimentam o modal de conversa)
    // além dos saldos e do badge de atenção — o modal lê turn/pendencyStatus do
    // detalhe e precisa re-renderizar com a pendência recém-aberta.
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.commissionBalances.all });
      void qc.invalidateQueries({ queryKey: queryKeys.commissionBalances.detail(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.commissionBalanceMessages.all(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.commissionAttention.count() });
    },
  });
}

/**
 * Limpa a pendência do saldo (motivo opcional, auditado quando informado).
 * PATCH /admin/commissions/balances/:id/pendency/clear.
 * Errors: 404 COMMISSION_BALANCE_NOT_FOUND, 422 COMMISSION_BALANCE_NO_PENDENCY.
 */
export function useClearBalancePendency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload?: ClearBalancePendencyPayload;
    }): Promise<CommissionBalance> => {
      const response = await apiClient.patch<ApiSuccess<CommissionBalance>>(
        `/admin/commissions/balances/${id}/pendency/clear`,
        payload ?? {},
      );
      return response.data.data;
    },
    // Mesma regra do set: limpar a pendência muda turn/pendencyStatus do detalhe
    // e fecha o tópico no fio — o modal precisa do detalhe + mensagens fresquinhos.
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.commissionBalances.all });
      void qc.invalidateQueries({ queryKey: queryKeys.commissionBalances.detail(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.commissionBalanceMessages.all(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.commissionAttention.count() });
    },
  });
}

// ─── Contestação + pendência com resposta (chat consultor↔financeiro) ─────────
// Lado admin/financeiro. Contrato: docs/contracts/ESCR-commission-dispute-pendency.md.

/**
 * Invalida o fio do saldo + a lista de saldos + o detalhe + o badge de atenção
 * do financeiro após uma mutação (resolver contestação, reabrir/limpar pendência).
 */
function useInvalidateAdminThread(balanceId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.commissionBalanceMessages.all(balanceId) });
    void qc.invalidateQueries({ queryKey: queryKeys.commissionBalances.all });
    void qc.invalidateQueries({ queryKey: queryKeys.commissionBalances.detail(balanceId) });
    void qc.invalidateQueries({ queryKey: queryKeys.commissionAttention.count() });
  };
}

/**
 * Lista paginada (asc) das mensagens de qualquer saldo (admin).
 * GET /admin/commissions/balances/:id/messages — permission commissions:manage.
 * 404 COMMISSION_BALANCE_NOT_FOUND.
 */
export function useBalanceMessages(
  balanceId: string | undefined,
  page = 1,
  options?: { enabled?: boolean },
) {
  const limit = 50;
  return useQuery({
    queryKey: queryKeys.commissionBalanceMessages.list(balanceId ?? '', page),
    queryFn: async (): Promise<PaginatedResponse<CommissionBalanceMessage>> => {
      const response = await apiClient.get<ApiSuccess<BackendList<CommissionBalanceMessage>>>(
        `/admin/commissions/balances/${balanceId}/messages`,
        { params: { page, limit } },
      );
      const raw = response.data.data;
      return {
        items: raw.rows,
        total: raw.count,
        page,
        limit,
        totalPages: Math.max(1, Math.ceil(raw.count / limit)),
      } satisfies PaginatedResponse<CommissionBalanceMessage>;
    },
    enabled: (options?.enabled ?? true) && !!balanceId,
  });
}

/**
 * Responde no fio do tópico ativo (kind derivado). Notifica o consultor.
 * POST /admin/commissions/balances/:id/messages — permission commissions:process.
 * Errors: 404 COMMISSION_BALANCE_NOT_FOUND, 422 COMMISSION_THREAD_NO_ACTIVE_TOPIC.
 */
export function usePostBalanceMessage(balanceId: string) {
  const invalidate = useInvalidateAdminThread(balanceId);
  return useMutation({
    mutationFn: async (payload: PostThreadMessageDto): Promise<CommissionBalanceMessage> => {
      const response = await apiClient.post<ApiSuccess<CommissionBalanceMessage>>(
        `/admin/commissions/balances/${balanceId}/messages`,
        payload,
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Resolve a contestação (palavra final do financeiro). dispute_status
 * open → resolved. Mensagem opcional. PATCH /admin/commissions/balances/:id/dispute/resolve.
 * Errors: 404 COMMISSION_BALANCE_NOT_FOUND, 422 COMMISSION_DISPUTE_NOT_OPEN.
 */
export function useResolveDispute(balanceId: string) {
  const invalidate = useInvalidateAdminThread(balanceId);
  return useMutation({
    mutationFn: async (payload: ResolveDisputeDto): Promise<CommissionBalance> => {
      const response = await apiClient.patch<ApiSuccess<CommissionBalance>>(
        `/admin/commissions/balances/${balanceId}/dispute/resolve`,
        payload,
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Palavra final do financeiro sobre uma pendência ABERTA: marca como resolvida
 * (pendency_status open → resolved). O backend grava mensagem-sistema no fio
 * (kind='pendency') e notifica o consultor. Observação opcional (note ≤500).
 * PATCH /admin/commissions/balances/:id/pendency/resolve.
 * Errors: 404 COMMISSION_BALANCE_NOT_FOUND, 422 COMMISSION_PENDENCY_NOT_OPEN.
 */
export function useResolvePendencyByAdmin(balanceId: string) {
  const invalidate = useInvalidateAdminThread(balanceId);
  return useMutation({
    mutationFn: async (
      payload: ResolveBalancePendencyByAdminPayload,
    ): Promise<CommissionBalance> => {
      const response = await apiClient.patch<ApiSuccess<CommissionBalance>>(
        `/admin/commissions/balances/${balanceId}/pendency/resolve`,
        payload,
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Reabre uma pendência que o consultor marcou como sanada. pendency_status
 * resolved → open. Mensagem opcional. PATCH /admin/commissions/balances/:id/pendency/reopen.
 * Errors: 404 COMMISSION_BALANCE_NOT_FOUND, 422 COMMISSION_PENDENCY_NOT_RESOLVED.
 */
export function useReopenPendency(balanceId: string) {
  const invalidate = useInvalidateAdminThread(balanceId);
  return useMutation({
    mutationFn: async (payload: ResolveDisputeDto): Promise<CommissionBalance> => {
      const response = await apiClient.patch<ApiSuccess<CommissionBalance>>(
        `/admin/commissions/balances/${balanceId}/pendency/reopen`,
        payload,
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Reagenda a previsão de pagamento do saldo (override admin de
 * expectedPaymentDate; reason opcional, auditado quando informado).
 * PATCH /admin/commissions/balances/:id/reschedule-payment.
 * Permissão commissions:process. Bloqueia paid/cancelled/superseded.
 * Errors: 404 COMMISSION_BALANCE_NOT_FOUND,
 *         422 COMMISSION_BALANCE_NOT_RESCHEDULABLE.
 */
export function useReschedulePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: ReschedulePaymentPayload;
    }): Promise<CommissionBalance> => {
      const response = await apiClient.patch<ApiSuccess<CommissionBalance>>(
        `/admin/commissions/balances/${id}/reschedule-payment`,
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.commissionBalances.all });
    },
  });
}

// ─── Liberação em massa + envio por e-mail (bulk) ─────────────────────────────

/**
 * Libera em lote os saldos selecionados (pending → released).
 * POST /admin/commissions/balances/bulk-release — idempotente/parcial: saldos
 * não-pending ou zerados voltam em `skipped` com o código de negócio, sem falhar
 * o lote. Permission commissions:process.
 */
export function useBulkReleaseBalances() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: BulkReleaseCommissionPayload,
    ): Promise<BulkReleaseCommissionResult> => {
      const response = await apiClient.post<ApiSuccess<BulkReleaseCommissionResult>>(
        '/admin/commissions/balances/bulk-release',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.commissionBalances.all });
    },
  });
}

/**
 * Enfileira o envio do relatório de comissão (PDF) por e-mail — um job por par
 * consultor×competência. POST /admin/commissions/email/send (202 Accepted): o
 * envio roda assíncrono no worker. Permission commissions:process.
 */
export function useSendCommissionEmail() {
  return useMutation({
    mutationFn: async (payload: SendCommissionEmailPayload): Promise<SendCommissionEmailResult> => {
      const response = await apiClient.post<ApiSuccess<SendCommissionEmailResult>>(
        '/admin/commissions/email/send',
        payload,
      );
      return response.data.data;
    },
  });
}
